import { useEffect, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Building2, FileText, Plus, Upload, Download, PauseCircle, XCircle, PlayCircle, Mail, Copy, ExternalLink, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const STATUS_LABEL = { active: "Activa", paused: "Pausada", disabled: "De baja" };
const STATUS_STYLE = {
  active: "bg-[#10B981]/10 text-[#047857] border-[#10B981]/30",
  paused: "bg-[#F59E0B]/10 text-[#92400E] border-[#F59E0B]/40",
  disabled: "bg-[#E11D48]/10 text-[#9F1239] border-[#E11D48]/30",
};
const EMPTY_ROW = { name: "", alias: "", code: "", staff_admin_name: "", staff_admin_email: "", company_admin_name: "", company_admin_email: "" };

function EditableEmail({ value, onSave, testid }) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(value || "");
  useEffect(() => { setV(value || ""); }, [value]);
  const commit = () => {
    const clean = v.trim().toLowerCase();
    setEditing(false);
    if (clean && clean !== (value || "").toLowerCase()) onSave(clean);
    else setV(value || "");
  };
  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="text-xs text-[#5C6A7D] hover:text-[#0A2540] hover:underline text-left"
        data-testid={testid}
      >
        {value || "— agregar correo —"}
      </button>
    );
  }
  return (
    <Input
      autoFocus
      value={v}
      onChange={(e) => setV(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") { setV(value || ""); setEditing(false); } }}
      className="h-8 text-xs rounded-sm"
      placeholder="correo@dominio.com"
      data-testid={`${testid}-input`}
    />
  );
}

function EditableText({ value, onSave, testid, placeholder = "— agregar —" }) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(value || "");
  useEffect(() => { setV(value || ""); }, [value]);
  const commit = () => {
    const clean = v.trim();
    setEditing(false);
    if (clean && clean !== (value || "").trim()) onSave(clean);
    else setV(value || "");
  };
  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="font-semibold text-[#0A2540] hover:text-[#10B981] hover:underline text-left"
        data-testid={testid}
      >
        {value || <span className="text-[#5C6A7D] font-normal">{placeholder}</span>}
      </button>
    );
  }
  return (
    <Input
      autoFocus
      value={v}
      onChange={(e) => setV(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") { setV(value || ""); setEditing(false); } }}
      className="h-8 text-sm rounded-sm"
      placeholder="Nombre"
      data-testid={`${testid}-input`}
    />
  );
}

function CompaniesTab() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [draft, setDraft] = useState({ ...EMPTY_ROW });
  const [busy, setBusy] = useState(false);
  const [invitations, setInvitations] = useState(null); // { company_name, items: [] }
  const [deleteTarget, setDeleteTarget] = useState(null); // full company row

  const load = () => api.get("/companies").then(({ data }) => setRows(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const showInvitations = (company_name, items) => {
    if (!items || items.length === 0) return;
    setInvitations({ company_name, items });
    toast.success(
      items.length === 1
        ? `Enlace de activación generado para ${items[0].role_label.toLowerCase()}.`
        : `${items.length} enlaces de activación generados.`,
    );
  };

  const setStatus = async (id, status) => {
    try { await api.patch(`/companies/${id}`, { status }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Falló"); }
  };

  const patchField = async (id, field, value) => {
    try {
      const { data } = await api.patch(`/companies/${id}`, { [field]: value });
      showInvitations(data.name, data.invitations || []);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Falló"); }
  };

  const addDraft = async () => {
    if (!draft.name.trim()) { toast.error("El nombre de la compañía es obligatorio"); return false; }
    const { data } = await api.post("/companies", draft);
    showInvitations(data.name, data.invitations || []);
    setDraft({ ...EMPTY_ROW });
    await load();
    return true;
  };

  const saveAndExit = async () => {
    setBusy(true);
    try {
      const hasDraft = Object.values(draft).some((v) => (v || "").trim());
      if (hasDraft) { const ok = await addDraft(); if (!ok) { setBusy(false); return; } }
      toast.success("Cambios guardados");
      nav("/");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo guardar");
    } finally { setBusy(false); }
  };

  const th = "px-3 py-2 text-left text-[10px] uppercase tracking-[0.12em] text-[#5C6A7D] font-semibold whitespace-nowrap";
  const td = "px-3 py-3 text-sm text-[#0A2540] align-top";

  return (
    <div className="space-y-4" data-testid="companies-tab">
      <div className="bg-white border border-[#E2E8F0] rounded-sm overflow-x-auto">
        <table className="w-full min-w-[1100px]">
          <thead className="bg-[#FAFAFA] border-b border-[#E2E8F0]">
            <tr>
              <th className={th}>Compañía</th>
              <th className={th}>Alias</th>
              <th className={th}>Código</th>
              <th className={th}>Admin de staff</th>
              <th className={th}>Admin de compañía</th>
              <th className={th}>Alta</th>
              <th className={th}>Usuarios</th>
              <th className={th}>Activos</th>
              <th className={th}>Interac.</th>
              <th className={th}>Acción</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E2E8F0]">
            {rows.map((c) => (
              <tr key={c.id} data-testid={`company-row-${c.id}`}>
                <td className={td}>
                  <div className="font-semibold">{c.name}</div>
                  <span className={`inline-flex items-center px-1.5 py-0.5 mt-1 text-[10px] uppercase tracking-[0.1em] font-bold rounded-sm border ${STATUS_STYLE[c.status]}`}>{STATUS_LABEL[c.status]}</span>
                </td>
                <td className={td}>{c.alias || "—"}</td>
                <td className={td}>{c.code || "—"}</td>
                <td className={td}>
                  <EditableText
                    value={c.staff_admin_name || ""}
                    onSave={(v) => patchField(c.id, "staff_admin_name", v)}
                    testid={`staff-name-${c.id}`}
                    placeholder="— agregar nombre —"
                  />
                  <EditableEmail
                    value={c.staff_admin_email || ""}
                    onSave={(v) => patchField(c.id, "staff_admin_email", v)}
                    testid={`staff-email-${c.id}`}
                  />
                </td>
                <td className={td}>
                  <EditableText
                    value={c.company_admin_name || ""}
                    onSave={(v) => patchField(c.id, "company_admin_name", v)}
                    testid={`company-name-${c.id}`}
                    placeholder="— agregar nombre —"
                  />
                  <EditableEmail
                    value={c.company_admin_email || ""}
                    onSave={(v) => patchField(c.id, "company_admin_email", v)}
                    testid={`company-email-${c.id}`}
                  />
                </td>
                <td className={td}>{c.registered_at || "—"}</td>
                <td className={td}>{c.users_count}</td>
                <td className={td}>{c.assets_count}</td>
                <td className={td}>{c.interactions_count}</td>
                <td className={td}>
                  <div className="flex flex-col gap-1.5">
                    <button onClick={() => setStatus(c.id, "paused")} className="inline-flex items-center gap-1 text-xs text-[#92400E] hover:underline" data-testid={`company-pause-${c.id}`}><PauseCircle size={13} /> Pausar</button>
                    <button onClick={() => setStatus(c.id, "disabled")} className="inline-flex items-center gap-1 text-xs text-[#9F1239] hover:underline" data-testid={`company-disable-${c.id}`}><XCircle size={13} /> Dar de baja</button>
                    <button onClick={() => setStatus(c.id, "active")} className="inline-flex items-center gap-1 text-xs text-[#047857] hover:underline" data-testid={`company-reactivate-${c.id}`}><PlayCircle size={13} /> Reactivar</button>
                    <button onClick={() => setDeleteTarget(c)} className="inline-flex items-center gap-1 text-xs font-semibold text-[#9F1239] hover:text-[#E11D48] hover:underline mt-1 pt-1.5 border-t border-[#E2E8F0]" data-testid={`company-delete-${c.id}`}><Trash2 size={13} /> Eliminar compañía</button>
                  </div>
                </td>
              </tr>
            ))}
            {/* Fila inferior para agregar una compañía nueva */}
            <tr className="bg-[#FAFAFA]/60" data-testid="company-new-row">
              <td className="px-3 py-2"><Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Nombre" className="h-9 rounded-sm" data-testid="new-company-name" /></td>
              <td className="px-3 py-2"><Input value={draft.alias} onChange={(e) => setDraft({ ...draft, alias: e.target.value })} placeholder="Alias" className="h-9 rounded-sm w-24" /></td>
              <td className="px-3 py-2"><Input value={draft.code} onChange={(e) => setDraft({ ...draft, code: e.target.value })} placeholder="Código" className="h-9 rounded-sm w-24" /></td>
              <td className="px-3 py-2 space-y-1">
                <Input value={draft.staff_admin_name} onChange={(e) => setDraft({ ...draft, staff_admin_name: e.target.value })} placeholder="Nombre" className="h-9 rounded-sm" />
                <Input value={draft.staff_admin_email} onChange={(e) => setDraft({ ...draft, staff_admin_email: e.target.value })} placeholder="Correo" className="h-9 rounded-sm" />
              </td>
              <td className="px-3 py-2 space-y-1">
                <Input value={draft.company_admin_name} onChange={(e) => setDraft({ ...draft, company_admin_name: e.target.value })} placeholder="Nombre" className="h-9 rounded-sm" />
                <Input value={draft.company_admin_email} onChange={(e) => setDraft({ ...draft, company_admin_email: e.target.value })} placeholder="Correo" className="h-9 rounded-sm" />
              </td>
              <td className="px-3 py-2 text-xs text-[#5C6A7D]">Automática</td>
              <td className="px-3 py-2 text-[#5C6A7D]">—</td>
              <td className="px-3 py-2 text-[#5C6A7D]">—</td>
              <td className="px-3 py-2 text-[#5C6A7D]">—</td>
              <td className="px-3 py-2">
                <Button onClick={addDraft} variant="outline" className="h-9 rounded-sm" data-testid="add-company-row"><Plus size={14} className="mr-1" /> Agregar</Button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="flex justify-end">
        <Button onClick={saveAndExit} disabled={busy} className="bg-[#0A2540] hover:bg-[#173B63] text-white rounded-sm h-11 px-6" data-testid="save-and-exit">Guardar y salir</Button>
      </div>

      <DeleteCompanyDialog company={deleteTarget} onClose={() => setDeleteTarget(null)} onDeleted={() => { setDeleteTarget(null); load(); }} />
      <InvitationsDialog data={invitations} onClose={() => setInvitations(null)} />
    </div>
  );
}

function DeleteCompanyDialog({ company, onClose, onDeleted }) {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const open = !!company;
  useEffect(() => { setTyped(""); }, [company?.id]);
  const expected = (company?.code || "").toUpperCase();
  const canDelete = expected && typed.trim().toUpperCase() === expected;
  const submit = async () => {
    if (!canDelete) return;
    setBusy(true);
    try {
      const { data } = await api.delete(`/companies/${company.id}`, { params: { code_confirm: typed.trim().toUpperCase() } });
      const d = data.deleted || {};
      toast.success(`Compañía "${data.name}" eliminada. ${d.users || 0} usuarios · ${d.projects || 0} proyectos · ${d.assets || 0} activos removidos.`);
      onDeleted?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No fue posible eliminar la compañía");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="rounded-sm sm:max-w-lg" data-testid="delete-company-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight flex items-center gap-2 text-[#9F1239]"><AlertTriangle size={20} /> Eliminar compañía permanentemente</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm text-[#0A2540]">
          <p>
            Vas a eliminar por completo <b>{company?.name}</b> (código <b>{expected || "—"}</b>).
            Esta acción borra en cascada:
          </p>
          <ul className="list-disc pl-6 text-xs text-[#5C6A7D] space-y-0.5">
            <li>Todos sus proyectos y sus fotos.</li>
            <li>Todos sus activos y sobrantes.</li>
            <li>Todas las cuentas de staff (incluyendo admin de staff y admin de compañía).</li>
            <li>Notificaciones, mensajes y solicitudes de reutilización asociadas.</li>
          </ul>
          <p className="text-xs text-[#9F1239] font-semibold">Esta acción no se puede deshacer.</p>
          <div className="space-y-1 pt-2 border-t border-[#E2E8F0]">
            <label className="text-xs uppercase tracking-[0.18em] text-[#5C6A7D]">Escribe el código de la compañía para confirmar</label>
            <Input
              autoFocus
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder={expected}
              className="rounded-sm h-10 font-mono uppercase"
              data-testid="delete-company-code-input"
            />
            <p className="text-[11px] text-[#5C6A7D]">Debe coincidir exactamente con <span className="font-mono font-semibold text-[#0A2540]">{expected || "—"}</span>.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} className="rounded-sm" data-testid="delete-company-cancel">Cancelar</Button>
          <Button
            onClick={submit}
            disabled={!canDelete || busy}
            className="rounded-sm bg-[#9F1239] hover:bg-[#E11D48] text-white disabled:opacity-40 disabled:pointer-events-none"
            data-testid="delete-company-confirm"
          >
            {busy ? "Eliminando…" : "Eliminar compañía"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function InvitationsDialog({ data, onClose }) {
  const open = !!data;
  const copy = async (link) => {
    try { await navigator.clipboard.writeText(link); toast.success("Enlace copiado."); }
    catch { toast.error("No se pudo copiar."); }
  };
  const items = data?.items || [];
  const allSent = items.length > 0 && items.every((i) => i.email_sent);
  const anyFailed = items.some((i) => !i.email_sent);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm sm:max-w-xl" data-testid="invitations-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">Enlaces de activación</DialogTitle>
        </DialogHeader>
        <div className="text-xs text-[#5C6A7D] mb-2">
          Compañía: <span className="font-semibold text-[#0A2540]">{data?.company_name}</span>.
          {" "}
          {allSent ? (
            <span data-testid="invitations-status-sent">Correo enviado correctamente a cada administrador. Este es el enlace de respaldo por si necesitas compartirlo manualmente. Vence en 48 h.</span>
          ) : anyFailed && items.some((i) => i.email_sent) ? (
            <span data-testid="invitations-status-mixed">Uno o más correos <b>no pudieron enviarse</b>: comparte el enlace manualmente con los administradores marcados. Vence en 48 h.</span>
          ) : (
            <span data-testid="invitations-status-failed">Envío por correo no disponible en este momento — comparte el enlace manualmente con cada administrador. Vence en 48 h.</span>
          )}
        </div>
        <div className="space-y-3 max-h-96 overflow-auto">
          {items.map((it, i) => (
            <div key={i} className="border border-[#E2E8F0] rounded-sm p-3 space-y-1.5" data-testid={`invitation-${it.role}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.16em] text-[#5C6A7D]">
                  <Mail size={12} /> {it.role_label}
                </div>
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded-sm border text-[10px] font-bold uppercase tracking-[0.14em] ${it.email_sent ? "bg-[#10B981]/10 text-[#047857] border-[#10B981]/40" : "bg-[#F59E0B]/10 text-[#92400E] border-[#F59E0B]/40"}`}
                  data-testid={`invitation-${it.role}-status`}
                >
                  {it.email_sent ? "Correo enviado" : "Compartir manualmente"}
                </span>
              </div>
              <div className="text-sm font-semibold text-[#0A2540]">{it.name || "—"} <span className="text-[#5C6A7D] font-normal">· {it.email}</span></div>
              <div className="flex items-center gap-2">
                <input readOnly value={it.reset_link} className="flex-1 h-8 text-xs px-2 rounded-sm border border-[#E2E8F0] bg-[#FAFAFA] text-[#0A2540] font-mono truncate" data-testid={`invitation-${it.role}-link`} />
                <Button size="sm" variant="outline" onClick={() => copy(it.reset_link)} className="rounded-sm h-8" data-testid={`invitation-${it.role}-copy`}>
                  <Copy size={12} className="mr-1" /> Copiar
                </Button>
                <a href={it.reset_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-[#0A2540] hover:text-[#10B981]"><ExternalLink size={12} /> Abrir</a>
              </div>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button onClick={onClose} className="rounded-sm bg-[#0A2540] hover:bg-[#173B63] text-white">Entendido</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DocumentsTab() {
  const [docs, setDocs] = useState([]);
  const [docType, setDocType] = useState("folleto");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/documents").then(({ data }) => setDocs(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const onUpload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!docType.trim()) { toast.error("Indica el tipo de documento"); return; }
    if (!file) { toast.error("Selecciona un archivo"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("doc_type", docType.trim().toLowerCase());
      fd.append("file", file);
      await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Documento actualizado");
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo subir");
    } finally { setUploading(false); }
  };

  const active = docs.filter((d) => d.active);
  const history = docs.filter((d) => !d.active);

  return (
    <div className="space-y-5" data-testid="documents-tab">
      <div className="bg-white border border-[#E2E8F0] rounded-sm p-5 space-y-3">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C6A7D]">Subir / reemplazar documento</div>
        <div className="grid sm:grid-cols-[200px_1fr_auto] gap-3 items-end">
          <div className="space-y-1.5">
            <label className="text-xs uppercase tracking-[0.18em] text-[#5C6A7D]">Tipo de documento</label>
            <Input value={docType} onChange={(e) => setDocType(e.target.value)} placeholder="folleto" className="h-11 rounded-sm" data-testid="doc-type-input" />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs uppercase tracking-[0.18em] text-[#5C6A7D]">Archivo</label>
            <input ref={fileRef} type="file" className="block w-full text-sm text-[#0A2540] file:mr-3 file:py-2 file:px-3 file:rounded-sm file:border-0 file:bg-[#EAF0F6] file:text-[#0A2540] file:text-sm" data-testid="doc-file-input" />
          </div>
          <Button onClick={onUpload} disabled={uploading} className="bg-[#0A2540] hover:bg-[#173B63] text-white rounded-sm h-11" data-testid="upload-doc-button"><Upload size={15} className="mr-1.5" /> Subir</Button>
        </div>
        <p className="text-xs text-[#5C6A7D]">El archivo se guarda como documento activo de su tipo. El anterior se conserva en el historial.</p>
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-sm">
        <div className="px-5 py-3 border-b border-[#E2E8F0] text-[10px] uppercase tracking-[0.2em] text-[#5C6A7D]">Documentos activos</div>
        <div className="divide-y divide-[#E2E8F0]">
          {active.length === 0 && <div className="px-5 py-8 text-center text-sm text-[#5C6A7D]">Sin documentos todavía.</div>}
          {active.map((d) => (
            <div key={d.id} className="px-5 py-4 flex items-center justify-between gap-3" data-testid={`active-doc-${d.doc_type}`}>
              <div className="flex items-center gap-3">
                <FileText size={18} className="text-[#0052CC]" />
                <div>
                  <div className="font-semibold text-[#0A2540]">{d.doc_type}</div>
                  <div className="text-xs text-[#5C6A7D]">{d.filename} · v{d.version}</div>
                </div>
              </div>
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/public/documents/${d.doc_type}/file`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0052CC] hover:underline"><Download size={15} /> Ver</a>
            </div>
          ))}
        </div>
      </div>

      {history.length > 0 && (
        <div className="bg-white border border-[#E2E8F0] rounded-sm">
          <div className="px-5 py-3 border-b border-[#E2E8F0] text-[10px] uppercase tracking-[0.2em] text-[#5C6A7D]">Historial de versiones</div>
          <div className="divide-y divide-[#E2E8F0]">
            {history.map((d) => (
              <div key={d.id} className="px-5 py-3 flex items-center gap-3 text-sm text-[#5C6A7D]">
                <FileText size={15} /> {d.doc_type} · {d.filename} · v{d.version}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AppAdmin() {
  const { user } = useAuth();
  if (user && user.role !== "app_admin") return <Navigate to="/" replace />;

  return (
    <div className="space-y-6" data-testid="app-admin-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#5C6A7D]">Administrador de app</div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-[#0A2540] tracking-tighter mt-1">Gestión de la plataforma.</h1>
      </div>
      <Tabs defaultValue="companies">
        <TabsList>
          <TabsTrigger value="companies" data-testid="tab-companies"><Building2 size={15} className="mr-1.5" /> Compañías</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents"><FileText size={15} className="mr-1.5" /> Documentos</TabsTrigger>
        </TabsList>
        <TabsContent value="companies" className="mt-5"><CompaniesTab /></TabsContent>
        <TabsContent value="documents" className="mt-5"><DocumentsTab /></TabsContent>
      </Tabs>
    </div>
  );
}
